import { useState, SetStateAction, Dispatch, useContext } from 'react';
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Box, FormControl, Select, InputLabel, MenuItem } from '@mui/material';
import CustomDialog from '../../../common/CustomDialog';
import { useAppDispatch } from '../../../../app/hooks';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../../features/snackbar/snackbarSlice';
import { UnitContext } from '../../../../context/UnitContext';
import { purchaserContent, createPdf } from '../../../../utils/Functions';
import { selectProject } from '../../../../features/project/projectSlice';
import { selectUser } from '../../../../features/auth/authSlice';
import { useCreateActivity } from '../../../../features/activity/activityHooks';
import { IDocuments } from '../../../../types/docusign';
import { IPurchaserInfo } from '../../../../types/CreateDealForm';
import { IMerge, IMergeData } from '../../../../types/merge';

const PurchaserDialog = (props: ChildProps) => {
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const storeDispatch = useAppDispatch();
  const createActivity = useCreateActivity();
  const { unit, filteredDeal, setFilteredDeal, originalPurchasers, updateDealDocuments, assignment } = useContext(UnitContext);
  const { dialogType, setDialogType, dialogOpen, setDialogOpen, purchaserIndex, setAddPurchaser, setShowAddPurchaser, type } = props;

  const [selectedPurchaserAmendment, setSelectedPurchaserAmendment] = useState<string>('');
  const [purchaserMergeTemplates, setPurchaserMergeTemplates] = useState<IMerge[]>([]);

  const [getPurchaserInformationMerges] = useLazyQuery<IMergeData>(MERGETEMPLATES, {
    variables: { filter: { project: project._id, type: 'PurchaserInformation' } },
    onCompleted: (data) => {
      setPurchaserMergeTemplates(data.mergeTemplateMany);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updatePurchaser] = useMutation(UPDATEPURCHASER, {
    onCompleted: (data) => {},
    onError: (err) => {
      if (err.message.includes('duplicate')) {
        storeDispatch(showErrorSnackbar('This email has already been used for another purchaser'));
      } else {
        storeDispatch(showErrorSnackbar(`Error with updating purchaser`));
      }
    },
  });

  const [createAmendment] = useMutation(CREATEDOCUMENT, {
    onCompleted: (data) => {
      let documentIds = filteredDeal.documents.map((document: IDocuments) => {
        return document._id;
      });
      updateDealDocuments({
        variables: { _id: filteredDeal._id, record: { documents: [...documentIds, data.documentCreateOne._id] } },
      });
      for (const [index, purchaser] of filteredDeal.purchasers.entries()) {
        if (purchaserContent(purchaser, originalPurchasers[index]) !== 'No Change') {
          const { _id, getUrl, putUrl, identifications, ...newObject } = purchaser;
          updatePurchaser({ variables: { _id: _id, record: newObject } });
          createActivity({
            project: project._id,
            user: user._id,
            deal: filteredDeal._id,
            title: `Amendment`,
            content: `Purchaser Information Amendment has been created. Purchaser ${index + 1} ${purchaserContent(
              purchaser,
              originalPurchasers[index]
            )}`,
          });
        }
      }
      setDialogOpen(false);
      setDialogType('');
      setFilteredDeal({ ...filteredDeal, documents: [...filteredDeal.documents, data.documentCreateOne] });
      storeDispatch(showSuccessSnackbar('Amendment has been created'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [deletePurchaser] = useMutation(DELETEPURCHASER, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Purchaser has been removed from the deal'));
      if (type === 'purchasers') {
        createActivity({
          project: project._id,
          user: user._id,
          deal: filteredDeal._id,
          title: `Delete Purchaser`,
          content: `Purchaser has been removed from the deal`,
        });
      }
      setFilteredDeal({
        ...filteredDeal,
        purchasers: data.dealUpdateById.record.purchasers,
        pendingAddPurchasers: data.dealUpdateById.record.pendingAddPurchasers,
        pendingDeletePurchasers: data.dealUpdateById.record.pendingDeletePurchasers,
      });
      if (type === 'pendingAddPurchasers' && !data.dealUpdateById.record.pendingAddPurchasers.length) {
        setShowAddPurchaser(type);
      }
      setDialogOpen(false);
      setDialogType('');
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [pendingDeletePurchaser] = useMutation(DELETEPURCHASER, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Purchaser has been added to Pending Delete Purchaser'));
      createActivity({
        project: project._id,
        user: user._id,
        deal: filteredDeal._id,
        title: `Pending Delete Purchaser`,
        content: `Purchaser has been added to Pending Delete Purchaser`,
      });
      setFilteredDeal({
        ...filteredDeal,
        pendingDeletePurchasers: data.dealUpdateById.record.pendingDeletePurchasers,
      });
      setDialogOpen(false);
      setDialogType('');
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const handleCloseSuccess = async () => {
    if (dialogType === 'purchaserAmendment') {
      if (!selectedPurchaserAmendment) {
        return storeDispatch(showErrorSnackbar('No Merge Template Found'));
      }

      let purchaserAmendment = purchaserMergeTemplates.find((mergeTemplate: IMerge) => mergeTemplate.name === selectedPurchaserAmendment);

      if (!purchaserAmendment) return storeDispatch(showErrorSnackbar('No Merge Template Found'));

      let pdf = await createPdf(
        purchaserAmendment,
        project,
        unit,
        originalPurchasers,
        [],
        [],
        filteredDeal.deposit,
        filteredDeal.options,
        null,
        null,
        null,
        null,
        filteredDeal,
        assignment ? assignment.assignees : filteredDeal.purchasers,
      );

      let sign = purchaserAmendment.signFields.map((sign: any) => {
        return {
          index: sign.index,
          key: sign.key,
          pageNumber: sign.pageNumber,
          x: sign.x,
          y: sign.y,
          title: sign.title
        };
      });
      const args = {
        status: 'Prepared',
        name: purchaserAmendment.name,
        project: project._id,
        deal: filteredDeal._id,
        signFields: sign,
        // signers,
        type: 'PurchaserInformation',
        isAPS: false,
        mergeTemplate: purchaserAmendment._id,
      };
      await createAmendment({ variables: { record: args, file: pdf } });
    } else if (dialogType === 'deletePurchaser') {
      if (originalPurchasers[purchaserIndex]) {
        pendingDeletePurchaser({
          variables: {
            _id: filteredDeal._id,
            record: {
              pendingDeletePurchasers: [
                ...filteredDeal.pendingDeletePurchasers.map((purchaser: IPurchaserInfo) => purchaser._id),
                originalPurchasers[purchaserIndex]._id,
              ],
            },
          },
        });
      } else return storeDispatch(showErrorSnackbar('Please save purchaser before creating Delete Purchaser Amendment'));
    } else if (dialogType === 'addPurchaser' && setShowAddPurchaser) {
      setShowAddPurchaser(type);
      setDialogOpen(false);
      setDialogType('');
    }
  };

  const handleCloseSecond = () => {
    if (dialogType === 'addPurchaser') {
      setAddPurchaser(true);
    } else if (dialogType === 'deletePurchaser') {
      let removedPurchaser = filteredDeal[type].filter((purchaser: IPurchaserInfo, index: number) => index !== purchaserIndex);
      if (filteredDeal[type][purchaserIndex].hasOwnProperty('_id')) {
        let purchaserIds = removedPurchaser
          .filter((purchaser: IPurchaserInfo) => {
            if (purchaser._id) {
              return purchaser._id;
            }
          })
          .map((purchaser: IPurchaserInfo) => purchaser._id);
        deletePurchaser({ variables: { _id: filteredDeal._id, record: { [type]: purchaserIds } } });
      } else {
        setFilteredDeal({
          ...filteredDeal,
          [type]: removedPurchaser,
        });
        if (!removedPurchaser.length) {
          setShowAddPurchaser(type);
        }
      }
    }
    setDialogOpen(false);
    setDialogType('');
  };

  const dialogContent = () => {
    if (dialogType === 'purchaserAmendment') {
      return (
        <div>
          <h4 style={{ margin: 0 }}>List of Changes</h4>
          <p>
            <em>
              Please note that this amendment is for changing the information for purchasers. This is not an amendment to add purchaser!
            </em>
          </p>
          {filteredDeal.purchasers.map((purchaser: IPurchaserInfo, index: number) => {
            if (purchaser._id) {
              return (
                <Box sx={{ mt: 2 }}>
                  <div>Purchaser {index + 1}</div>
                  {purchaserContent(purchaser, originalPurchasers[index])}
                </Box>
              );
            } else return null;
          })}
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="purchaser-information-label">Purchaser Information Amendments</InputLabel>
              <Select
                labelId="purchaser-information-label"
                id="purchaser-information-id"
                label="Purchaser Information Amendments"
                onOpen={() => getPurchaserInformationMerges()}
                onChange={(e: any) => setSelectedPurchaserAmendment(e.target.value)}
              >
                {purchaserMergeTemplates.map((mergeTemplate: IMerge, index: number) => {
                  return (
                    <MenuItem value={mergeTemplate.name} key={index}>
                      {mergeTemplate.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        </div>
      );
    }
  };

  let successButton = '';
  let dialogTitle = '';
  let secondOption = '';

  if (dialogType === 'purchaserAmendment') {
    successButton = 'Create Purchaser Information Amendment';
    dialogTitle = 'Please select a Purchaser Amendment';
  } else if (dialogType === 'deletePurchaser') {
    successButton = 'Yes';
    secondOption = 'No';
    dialogTitle = 'Will you be sending a Delete Purchaser Amendment?';
  } else if (dialogType === 'addPurchaser') {
    successButton = 'Yes';
    secondOption = 'No';
    dialogTitle = 'Will you be sending an Add Purchaser Amendment?';
  }

  return (
    <CustomDialog
      handleClose={() => {
        setDialogOpen(false);
        setDialogType('');
      }}
      handleCloseRemove={() => {
        setDialogOpen(false);
        setDialogType('');
      }}
      handleCloseSuccess={handleCloseSuccess}
      handleCloseSecond={handleCloseSecond}
      open={dialogOpen}
      removeButton={'Cancel'}
      successButton={type === 'purchasers' ? successButton : null}
      secondOption={secondOption ? secondOption : null}
      dialogContent={dialogContent()}
      dialogTitle={dialogTitle}
      maxWidth={'xl'}
    />
  );
};

interface ChildProps {
  dialogOpen: boolean;
  dialogType: string;
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
  setDialogType: Dispatch<SetStateAction<string>>;
  purchaserIndex: number;
  setAddPurchaser: Dispatch<SetStateAction<boolean>>;
  setShowAddPurchaser?: any;
  type: string;
}

const DELETEPURCHASER = gql`
  mutation dealUpdateById($_id: MongoID!, $record: UpdateByIdDealInput!) {
    dealUpdateById(_id: $_id, record: $record) {
      record {
        pendingDeletePurchasers {
          _id
          email
          firstName
          lastName
          identifications {
            _id
            name
            getUrl
          }
          corp
          sin
          dob
          streetAddress
          city
          province
          postalCode
          occupation
          employer
          purchaserType
          primaryPhone
          idType
          idNumber
          idExpiry
          unit
          idJurisdiction
          getUrl
          putUrl
          directors
          businessNumber
          signingOfficers {
            fullName
            dob
            sin
            primaryPhone
            streetAddress
            email
          }
        }
        pendingAddPurchasers {
          _id
          email
          firstName
          lastName
          identifications {
            _id
            name
            getUrl
          }
          corp
          sin
          dob
          streetAddress
          city
          province
          postalCode
          occupation
          employer
          purchaserType
          primaryPhone
          idType
          idNumber
          idExpiry
          unit
          idJurisdiction
          getUrl
          putUrl
          directors
          businessNumber
          signingOfficers {
            fullName
            dob
            sin
            primaryPhone
            streetAddress
            email
          }
        }
        purchasers {
          _id
          email
          firstName
          lastName
          identifications {
            _id
            name
            getUrl
          }
          corp
          sin
          dob
          streetAddress
          city
          province
          postalCode
          occupation
          employer
          purchaserType
          primaryPhone
          idType
          idNumber
          idExpiry
          unit
          idJurisdiction
          getUrl
          putUrl
          directors
          businessNumber
          signingOfficers {
            fullName
            dob
            sin
            primaryPhone
            streetAddress
            email
          }
        }
      }
    }
  }
`;

const CREATEDOCUMENT = gql`
  mutation documentCreateOne($record: CreateOneDocumentInput, $file: Upload) {
    documentCreateOne(record: $record, file: $file) {
      _id
      name
      project {
        _id
      }
      type
      getUrl
      status
      isAPS
      dsEnvelopeId
      mergeTemplate
    }
  }
`;

const MERGETEMPLATES = gql`
  query mergeTemplateMany($filter: FilterFindManyMergeTemplateInput) {
    mergeTemplateMany(filter: $filter) {
      _id
      project {
        _id
      }
      name
      mergeFields {
        key
        index
        pageNumber
        x
        y
        fontSize
        format
        wrap
      }
      signFields {
        key
        index
        pageNumber
        x
        y
        name
        title
      }
      default
      totalPages
      type
      getUrl
      putUrl
    }
  }
`;

const UPDATEPURCHASER = gql`
  mutation purchaserUpdateById($_id: MongoID!, $record: UpdateByIdPurchaserInput!) {
    purchaserUpdateById(_id: $_id, record: $record) {
      record {
        _id
        email
        firstName
        lastName
        identifications {
          _id
          name
          getUrl
        }
        sin
        dob
        streetAddress
        city
        province
        country
        postalCode
        occupation
        employer
        directors
        businessNumber
        signingOfficers {
          fullName
          dob
          sin
          primaryPhone
          streetAddress
          email
        }
        purchaserType
        primaryPhone
        idType
        idNumber
        idExpiry
        proofExpiry
        proofNumber
        proofType
        unit
        idJurisdiction
        putUrl
      }
    }
  }
`;

export default PurchaserDialog;
